// Карточка контрибьютора: кто это, как идут часы, что отклонили и почему,
// кто вёл человека раньше.
//
// Экран отвечает на один вопрос — «почему у него такие цифры»: сводка сверху,
// дневная динамика, список записей с причинами отказа. Отказы важнее принятых
// записей, поэтому они не прячутся за фильтр.
import { useEffect, useState } from 'preact/hooks';
import { api, type VistaContributorRes, type VistaStaffMe as Me } from '../api';
import { genPassword } from './ManagePage';
import type { T } from '../i18n';
import { InviteModal } from '../components/InviteModal';

const fmtH = (h: number): string => h.toFixed(h >= 100 ? 0 : 1);
const fmtMoney = (cents: number | null): string =>
  cents == null ? '—' : `$${(cents / 100).toFixed(2)}`;
const fmtDur = (sec: number | null): string => {
  if (!sec) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `0:${String(s).padStart(2, '0')}`;
};
const fmtDate = (ms: number | null): string =>
  ms ? new Date(ms).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }) : '—';
const fmtDateTime = (ms: number | null): string =>
  ms
    ? new Date(ms).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

// Ярлыки статуса дублируют PoolPage намеренно: экран может открываться по
// прямой ссылке, и тянуть общий модуль ради двух строк дороже, чем держать их
// рядом с местом использования.
/** Событие журнала → человеческое название. Незнакомые коды показываем как
 *  есть: лучше сырой код, чем пустая ячейка. */


// Contributor cabinets are en/es/pt only — 'ru' is the STAFF language
// (packages/contracts/src/vista/manage.ts, migration 0001's two different
// ui_lang defaults). Do not add 'ru' here without widening the contract AND
// the "language is limited to the contributor set" test first.
const UI_LANG_LABEL: Record<string, string> = {
  en: 'English',
  es: 'Español',
  pt: 'Português',
};
const UI_LANGS = Object.keys(UI_LANG_LABEL);

// The clip lifecycle in one place. Deliberately the full ladder rather than
// three buckets: «снято» and «отгружено» are different work on our side, and
// collapsing them hides where a batch is actually stuck.

export function ContributorPage({ me, id, t }: { me: Me; id: string; t: T }) {
  const [data, setData] = useState<VistaContributorRes | null | 'error'>(null);
  const [tab, setTab] = useState<'all' | 'rejected'>('all');
  // Cabinet password reset (0004): the pool's admin issues new credentials,
  // shown once with a copy button.
  const [creds, setCreds] = useState<{ email: string; password: string } | null>(null);
  const [pwdBusy, setPwdBusy] = useState(false);
  // F5 — ссылка входа вместо диктовки пароля.
  const [inviteOpen, setInviteOpen] = useState(false);

  // F7 — card edit: name/country/city/phone/uiLang. Kept as one small form
  // rather than per-field inline edits (unlike the pool table's rate cell) —
  // these fields change together when someone corrects an onboarding typo.
  const [form, setForm] = useState({ name: '', country: '', city: '', phone: '', uiLang: 'en' });
  const [cardBusy, setCardBusy] = useState(false);
  const [cardErr, setCardErr] = useState<string | null>(null);
  const [cardSaved, setCardSaved] = useState(false);

  // F7 — block/unblock. Blocking asks for a second tap (inline, not a native
  // confirm — brandbook has no native-dialog styling); unblocking is not
  // destructive and fires straight away.
  const [blockConfirm, setBlockConfirm] = useState(false);
  const [blockBusy, setBlockBusy] = useState(false);
  const [blockErr, setBlockErr] = useState<string | null>(null);

  const reload = () => api.contributor(id).then(setData).catch(() => setData('error'));

  const resetPassword = async (email: string) => {
    setPwdBusy(true);
    try {
      const password = genPassword();
      await api.patchContributor(id, { password });
      setCreds({ email, password });
    } catch {
      setCreds(null);
    } finally {
      setPwdBusy(false);
    }
  };

  const saveCard = async (e: Event) => {
    e.preventDefault();
    setCardBusy(true);
    setCardErr(null);
    setCardSaved(false);
    try {
      await api.patchContributor(id, {
        name: form.name.trim() || null,
        country: form.country.trim() ? form.country.trim().toUpperCase() : null,
        city: form.city.trim() || null,
        phone: form.phone.trim() || null,
        uiLang: form.uiLang,
      });
      setCardSaved(true);
      await reload();
    } catch (e) {
      setCardErr(e instanceof Error ? e.message : String(e));
    } finally {
      setCardBusy(false);
    }
  };

  // F3 — привязка съёмочного кабинета к штабному аккаунту. Без неё человек с
  // разными почтами (рабочая в штабе, отдельная на съёмку) не видит
  // переключателя кабинетов вовсе: связка держалась на совпадении почты.
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkErr, setLinkErr] = useState<string | null>(null);
  const linkToStaff = async (email: string | null) => {
    setLinkBusy(true);
    setLinkErr(null);
    try {
      await api.patchContributor(id, { linkUserEmail: email });
      await reload();
    } catch (e) {
      setLinkErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLinkBusy(false);
    }
  };

  const setStatus = async (status: 'blocked' | 'active') => {
    setBlockBusy(true);
    setBlockErr(null);
    try {
      await api.patchContributor(id, { status });
      setBlockConfirm(false);
      await reload();
    } catch (e) {
      setBlockErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBlockBusy(false);
    }
  };

  useEffect(() => {
    setData(null);
    api
      .contributor(id)
      .then(setData)
      .catch(() => setData('error'));
  }, [id]);

  // Form starts empty until the card loads, then follows the server row —
  // re-syncing after every save (reload() above) so a second edit never
  // fights stale local state.
  useEffect(() => {
    if (data && data !== 'error') {
      setForm({
        name: data.person.name ?? '',
        country: data.person.country ?? '',
        city: data.person.city ?? '',
        phone: data.person.phone ?? '',
        uiLang: UI_LANGS.includes(data.person.uiLang) ? data.person.uiLang : 'en',
      });
    }
  }, [data]);

  if (data === 'error') {
    return (
      <div class="empty">
        {t('cp.notFound')} <a href="#/">{t('cp.backToPool')}</a>
      </div>
    );
  }
  if (!data) return <div class="empty">{t('cp.loading')}</div>;

  const p = data.person;
  const rejected = data.recent.filter((r) => r.status === 'rejected');
  const list = tab === 'rejected' ? rejected : data.recent;
  // Дневная кривая приходит с бэкенда свежими днями вперёд — для графика
  // разворачиваем, чтобы время шло слева направо.
  const days = [...data.daily].reverse();
  const maxDay = Math.max(1, ...days.map((d) => d.filmedHours));

  return (
    <>
      <div class="crumbs">
        <a href="#/">{t('cp.crumb')}</a>
      </div>

      <div class="card-head">
        <div>
          <h2>{p.name ?? p.email}</h2>
          <div class="muted">
            {p.name ? `${p.email} · ` : ''}
            {p.country ?? t('cp.noCountry')}
            {p.phone ? ` · ${p.phone}` : ''}
          </div>
        </div>
        <div class="card-chips">
          <span class={`status-chip ${p.status}`}>{t(`pool.status.${p.status}`)}</span>
          {me.role !== 'viewer' && (
            <>
              {/* Ссылка стоит ПЕРЕД паролем: это основной способ завести
                  человека, пароль — запасной для тех, у кого нет почты. */}
              <button type="button" class="btn-soft" onClick={() => setInviteOpen(true)}>
                {t('cp.inviteLink')}
              </button>
              <button type="button" class="btn-soft" disabled={pwdBusy} onClick={() => resetPassword(p.email)}>
                {pwdBusy ? t('cp.pwd.generating') : t('cp.pwd.new')}
              </button>
              {/* Блокировка/разблокировка (F7): 'active' ↔ 'blocked'. Блокировка
                  просит второе нажатие — это единственное действие на карточке,
                  которое отрезает человеку вход. */}
              {p.status === 'blocked' ? (
                <button type="button" class="btn-soft" disabled={blockBusy} onClick={() => setStatus('active')}>
                  {blockBusy ? t('cp.unblocking') : t('cp.unblock')}
                </button>
              ) : blockConfirm ? (
                <span class="inline-confirm">
                  {t('cp.blockConfirm')}
                  <button type="button" class="btn-danger" disabled={blockBusy} onClick={() => setStatus('blocked')}>
                    {blockBusy ? t('cp.blocking') : t('cp.yes')}
                  </button>
                  <button type="button" class="btn-ghost" disabled={blockBusy} onClick={() => setBlockConfirm(false)}>
                    {t('cp.cancel')}
                  </button>
                </span>
              ) : (
                <button type="button" class="btn-danger" onClick={() => setBlockConfirm(true)}>
                  {t('cp.block')}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {me.role !== 'viewer' && (
        <div class="wb-card link-row">
          <div>
            <b>{t('cp.cabinet')}</b>{' '}
            {p.linkedUserEmail ? (
              <>
                {t('cp.linked')} <b>{p.linkedUserEmail}</b> {t('cp.linkedTail')}
              </>
            ) : (
              t('cp.notLinked')
            )}
          </div>
          <div class="link-row-actions">
            {linkErr && <span class="msg err">{linkErr}</span>}
            {p.linkedUserEmail === me.email ? (
              <button type="button" class="btn-soft" disabled={linkBusy} onClick={() => linkToStaff(null)}>
                {linkBusy ? t('cp.unlinking') : t('cp.unlink')}
              </button>
            ) : (
              <button type="button" class="btn-soft" disabled={linkBusy} onClick={() => linkToStaff(me.email)}>
                {linkBusy ? t('cp.linking') : t('cp.itsMe')}
              </button>
            )}
          </div>
        </div>
      )}

      {inviteOpen && (
        <InviteModal
          t={t}
          contributorId={id}
          name={p.name ?? p.email}
          onClose={() => setInviteOpen(false)}
        />
      )}

      {creds && (
        <CredsBlock t={t} email={creds.email} password={creds.password} onClose={() => setCreds(null)} />
      )}

      {me.role !== 'viewer' && data.activity.length > 0 && (
        <div class="panel">
          <h3>{t('cp.journal')}</h3>
          {/* Журнал нужен ровно для одного разговора — «кто это сделал».
              Показываем последние события: ставка, блокировка, вердикты,
              выплаты. Без него спор упирается в «я не менял». */}
          <div class="tablewrap">
            <table>
              <thead>
                <tr>
                  <th>{t('cp.th.when')}</th>
                  <th>{t('cp.th.who')}</th>
                  <th>{t('cp.th.what')}</th>
                  <th>{t('cp.th.details')}</th>
                </tr>
              </thead>
              <tbody>
                {data.activity.map((a, i) => (
                  <tr key={i}>
                    <td>{fmtDateTime(a.createdAt)}</td>
                    <td>{a.actorName ?? <span class="muted">{t('cp.system')}</span>}</td>
                    <td>{t(`cp.act.${a.event}`)}</td>
                    <td class="muted">{a.detail ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {blockErr && (
          <div class="strip bad">
            {t('cp.saveFailed')} {blockErr}
          </div>
        )}

      {p.rateCents == null && (
        <div class="strip warn">
          {t('cp.noRateWarn')}
        </div>
      )}

      {me.role !== 'viewer' && (
        <div class="panel">
          <h3>{t('cp.data')}</h3>
          <form class="edit-grid" onSubmit={saveCard}>
            <label class="field">
              <span>{t('cp.name')}</span>
              <input
                type="text"
                value={form.name}
                onInput={(e) => setForm({ ...form, name: (e.target as HTMLInputElement).value })}
              />
            </label>
            <label class="field">
              <span>{t('cp.country')}</span>
              <input
                type="text"
                maxLength={2}
                placeholder="US"
                value={form.country}
                onInput={(e) =>
                  setForm({ ...form, country: (e.target as HTMLInputElement).value.toUpperCase() })
                }
              />
            </label>
            <label class="field">
              <span>{t('cp.city')}</span>
              <input
                type="text"
                value={form.city}
                onInput={(e) => setForm({ ...form, city: (e.target as HTMLInputElement).value })}
              />
            </label>
            <label class="field">
              <span>{t('cp.phone')}</span>
              <input
                type="tel"
                value={form.phone}
                onInput={(e) => setForm({ ...form, phone: (e.target as HTMLInputElement).value })}
              />
            </label>
            <label class="field">
              <span>{t('cp.uiLang')}</span>
              <select
                value={form.uiLang}
                onChange={(e) => setForm({ ...form, uiLang: (e.target as HTMLSelectElement).value })}
              >
                {UI_LANGS.map((l) => (
                  <option value={l} key={l}>{UI_LANG_LABEL[l]}</option>
                ))}
              </select>
            </label>
            <div class="edit-actions">
              <button type="submit" class="btn-primary" disabled={cardBusy}>
                {cardBusy ? t('cp.saving') : t('cp.save')}
              </button>
              {cardSaved && <span class="muted">{t('cp.saved')}</span>}
            </div>
          </form>
          {cardErr && (
            <div class="strip bad">
              {t('cp.saveFailed')} {cardErr}
            </div>
          )}
        </div>
      )}

      <div class="tiles">
        <div class="tile">
          <div class="n">{fmtH(p.acceptedHours)} {t('pool.hours')}</div>
          <div class="t">{t('pool.tile.accepted')}</div>
        </div>
        <div class="tile">
          <div class="n">{fmtH(p.pendingHours)} {t('pool.hours')}</div>
          <div class="t">{t('pool.tile.pending')}</div>
        </div>
        <div class="tile">
          <div class="n">{fmtH(p.rejectedHours)} {t('pool.hours')}</div>
          <div class="t">{t('pool.tile.rejected')}</div>
        </div>
        <div class="tile">
          <div class="n">{fmtH(p.filmedHours)} {t('pool.hours')}</div>
          <div class="t">{t('cp.rev.filmed')}</div>
        </div>
        <div class="tile">
          <div class="n">{p.earnedCents == null ? '—' : fmtMoney(p.earnedCents)}</div>
          <div class="t">
            {p.rateCents == null
              ? t('cp.noRate')
              : t('cp.earnedAt', { rate: `$${(p.rateCents / 100).toFixed(2)}` })}
          </div>
        </div>
        {/* Client revenue — the worker sends it to managers only. */}
        {p.clientCents != null && (
          <div class="tile">
            <div class="n">{fmtMoney(p.clientCents)}</div>
            <div class="t">{t('pool.tile.revenue')}</div>
          </div>
        )}
      </div>

      {days.length > 0 && (
        <div class="panel">
          <h3>{t('cp.byDay')}</h3>
          {/* Столбики, а не библиотека графиков: нужен один взгляд «ровно
              работает или рывками», ради этого тянуть зависимость незачем. */}
          <div class="bars">
            {days.map((d) => (
              <div class="bar-col" key={d.day} title={`${d.day}: ${fmtH(d.filmedHours)} ${t('pool.hours')}`}>
                <div class="bar-stack" style={{ height: `${(d.filmedHours / maxDay) * 100}%` }}>
                  <div class="bar-rej" style={{ flex: d.rejectedHours }} />
                  <div class="bar-pend" style={{ flex: d.pendingHours }} />
                  <div class="bar-acc" style={{ flex: d.acceptedHours }} />
                </div>
                <div class="bar-label">{d.day.slice(8)}</div>
              </div>
            ))}
          </div>
          <div class="legend">
            <span>
              <i class="sw acc" /> {t('pool.tile.accepted')}
            </span>
            <span>
              <i class="sw pend" /> {t('pool.tile.pending')}
            </span>
            <span>
              <i class="sw rej" /> {t('pool.tile.rejected')}
            </span>
          </div>
        </div>
      )}

      <div class="panel">
        <div class="panel-head">
          <h3>{t('cp.records')}</h3>
          <div class="seg">
            <button class={tab === 'all' ? 'on' : ''} onClick={() => setTab('all')}>
              {t('cp.tab.all', { n: data.recent.length })}
            </button>
            <button
              class={tab === 'rejected' ? 'on' : ''}
              onClick={() => setTab('rejected')}
            >
              {t('cp.tab.rejected', { n: rejected.length })}
            </button>
          </div>
        </div>

        {list.length === 0 ? (
          <div class="empty">{tab === 'rejected' ? t('cp.noRejected') : t('cp.noRecords')}</div>
        ) : (
          <div class="tablewrap">
            <table>
              <thead>
                <tr>
                  <th>{t('cp.th.file')}</th>
                  <th>{t('cp.th.filmed')}</th>
                  <th>{t('cp.th.length')}</th>
                  <th>{t('cp.th.status')}</th>
                  <th>{t('cp.th.topic')}</th>
                  <th>{t('cp.th.integrity')}</th>
                  <th>{t('cp.th.reason')}</th>
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id}>
                    <td>{r.fileName ?? <span class="muted">{t('cp.noName')}</span>}</td>
                    <td>{fmtDateTime(r.recordedAt ?? r.uploadedAt)}</td>
                    <td>{fmtDur(r.durationSeconds)}</td>
                    <td>
                      <span class={`status-chip ${r.status === 'accepted' ? 'active' : r.status === 'rejected' ? 'fraud' : 'pend'}`}>
                        {t(`cp.rev.${r.status}`)}
                      </span>
                    </td>
                    <td>{r.taskSlug ?? <span class="muted">—</span>}</td>
                    {/* Насколько запись дотянула до собственного таймера. Ниже
                        порога — это пересъёмка, а не брак оператора: путать
                        технический сбой с плохой работой нельзя. */}
                    <td>{r.integrityRatio == null ? '—' : `${Math.round(r.integrityRatio * 100)}%`}</td>
                    <td class="reason">{r.rejectReason ?? <span class="muted">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data.ownership.length > 0 && (
        <div class="panel">
          <h3>{t('cp.owners')}</h3>
          <ul class="owners">
            {data.ownership.map((o, i) => (
              <li key={i}>
                <b>{o.ownerName ?? '—'}</b>{' '}
                <span class="muted">
                  {fmtDate(o.fromAt)} — {o.toAt == null ? t('cp.now') : fmtDate(o.toAt)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

// Local creds block (login + password one-liner): the ManagePage panel is about
// staff and carries its wording; this one is the contributor cabinet.
function CredsBlock({
  t,
  email,
  password,
  onClose,
}: {
  t: T;
  email: string;
  password: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const text = `${email} ${password}`;
  return (
    <div class="creds">
      <pre>{text}</pre>
      <div class="creds-actions">
        <button
          type="button"
          class="btn-primary"
          onClick={() => navigator.clipboard.writeText(text).then(() => setCopied(true))}
        >
          {copied ? t('cp.creds.copied') : t('cp.creds.copy')}
        </button>
        <button type="button" class="btn-ghost" onClick={onClose}>
          {t('cp.creds.hide')}
        </button>
      </div>
      <div class="muted">{t('cp.creds.note')}</div>
    </div>
  );
}
